import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LogOut,
  X,
  LayoutDashboard,
  Users,
  Stethoscope,
  UserPlus,
  Building2,
  Pill,
  FlaskConical,
  CreditCard,
  ChevronRight,
  Settings,
  Settings2,
  LucideSettings,
  Building,
  History,
} from "lucide-react";
import { Button } from "../../ui/button";
import { doAdminLogout } from "../../../services/admin/adminAuthService";
import { showSuccessToast } from "../../../utils/toast-utils";
import { useState } from "react";
import ViewAdminProfile from "../../viewsModals/viewAdminProfileModal";
import useAdminProfile from "../../../store/admin/profile";

const Sidebar = ({ setOpen, isPhone }) => {
  const location = useLocation();
  const isActive = (path) => location.pathname === path;
  const navigate = useNavigate();

  const { profileData, isProfileClicked, setIsProfileClicked } =
    useAdminProfile();

  const handleLogout = async () => {
    try {
      const res = await doAdminLogout();
      if (res && (res.status === 200 || res?.data?.status === 200)) {
        showSuccessToast("Logged out successfully");
        navigate("/admin-login", { replace: true });
      }
    } catch (error) {
      console.error(error);
    }
  };

  // Grouped Navigation Structure
  const menuGroups = [
    {
      group: "Overview",
      items: [
        {
          name: "Dashboard",
          path: "/admin-dashboard",
          icon: <LayoutDashboard size={20} />,
        },
      ],
    },
    {
      group: "Access Management",
      items: [
        {
          name: "Create Role",
          path: "/create-role",
          icon: <UserPlus size={20} />,
        },
        {
          name: "Role Permissions",
          path: "/role-permission",
          icon: <Settings size={20} />,
        },
        // {
        //   name: "Employee Permissions",
        //   path: "/employee-permission",
        //   icon: <LucideSettings size={20} />,
        // },
      ],
    },
    {
      group: "User Management",
      items: [
        {
          name: "Employees",
          path: "/create-employee",
          icon: <Users size={20} />,
        },
        {
          name: "Doctors",
          path: "/create-doctor",
          icon: <Stethoscope size={20} />,
        },
        {
          name: "Patients",
          path: "/create-patient",
          icon: <UserPlus size={20} />,
        },
      ],
    },
    {
      group: "Hospital Infrastructure",
      items: [
        {
          name: "Branches",
          path: "/create-branch",
          icon: <Building2 size={20} />,
        },
        {
          name: "Manage Company",
          path: "/manage-company",
          icon: <Building size={20} />,
        },
        {
          name: "Pharmacy",
          path: "/create-pharmacy",
          icon: <Pill size={20} />,
        },
        {
          name: "Lab Diagnostic",
          path: "/create-lab-diagnostic",
          icon: <FlaskConical size={20} />,
        },
        {
          name: "Health Card Master",
          path: "/health-card-master",
          icon: <CreditCard size={20} />,
        },
        {
          name: "Login History",
          path: "/admin-login-history",
          icon: <History size={20} />,
        },
      ],
    },
  ];

  return (
    <div className="h-full flex flex-col text-slate-300 bg-[#0a1f44] overflow-hidden">
      {/*close Sidebar */}
      <div className="hidden md:flex items-center justify-between px-6 py-4 border-b border-white/10">
        <span className="font-semibold text-white text-lg tracking-wide">
          Menu
        </span>

        <button
          onClick={() => setOpen(false)}
          className="group relative flex items-center justify-center w-9 h-9 
               rounded-xl bg-white/5 backdrop-blur-md 
               border border-white/10 
               transition-all duration-200
               hover:bg-white/10 hover:scale-105 active:scale-95"
        >
          <X
            size={18}
            className="text-white/70 group-hover:text-white transition-colors"
            strokeWidth={2.2}
          />
        </button>
      </div>
      {/* Navigation Scroll Area */}
      <div className="flex-1 overflow-y-auto py-6 custom-scrollbar">
        {menuGroups.map((group, idx) => (
          <div key={idx} className="mb-6">
            <h3 className="px-8 text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-3">
              {group.group}
            </h3>
            <ul className="space-y-1 px-4">
              {group.items.map((item) => (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    onClick={() => {
                      setOpen && isPhone && setOpen(false);
                    }}
                    className={`
                      flex items-center justify-between px-4 py-3 rounded-lg transition-all duration-200 group
                      ${
                        isActive(item.path)
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-900/20"
                          : "hover:bg-white/5 hover:text-white"
                      }
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`${isActive(item.path) ? "text-white" : "text-slate-400 group-hover:text-blue-400"}`}
                      >
                        {item.icon}
                      </span>
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    {isActive(item.path) && (
                      <ChevronRight size={14} className="text-white/70" />
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      {/* Footer / User Quick Info */}
      <button
        type="button"
        onClick={() => setIsProfileClicked(true)}
        className="p-4 border-t  border-white/10 bg-[#081a3a]/50"
      >
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-white/5 border border-white/5">
          <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-xs font-bold text-white">
            AD
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white truncate">
              {profileData?.name}
            </p>
            <p className="text-xs text-slate-500 truncate">Main Branch</p>
          </div>
        </div>

        <div className=" md:hidden pt-4">
          <Button
            onClick={handleLogout}
            className="w-full justify-start gap-3 bg-red-500/10 text-red-400 hover:bg-red-500 hover:text-white"
          >
            <LogOut size={18} /> Logout
          </Button>
        </div>
      </button>
    </div>
  );
};

export default Sidebar;
